import { create } from 'zustand';
import { clearQualificationDraft } from './qualificationDraft';
import { persist } from 'zustand/middleware';
import { setStoreContext } from '../lib/dtpAnalytics';

export interface DtpCartItem {
  productId: string;
  productName: string;
  price: number; // patient-facing price
  /**
   * Which drug this is, from doseFamilyKey(). Doses are separate products, so
   * a patient switching dose adds a second SKU of the same drug; with this set,
   * the new dose replaces the old one instead of both ending up in the cart
   * (two doses of one program fail checkout with a confusing plan error).
   */
  family?: string;
  /**
   * A program dose chosen before checkout (Change dose), so checkout opens on
   * that plan instead of asking again. Not health information: just which
   * plan of the program's published list.
   */
  plan?: { programId: string; planId: string; path: 'starter' | 'continuation' };
}

/** The key two doses of one drug share: the family slug, else the drug name. */
export function doseFamilyKey(product: { drugFamilySlug?: string | null; drugName?: string | null }): string | undefined {
  const key = (product.drugFamilySlug || product.drugName || '').trim().toLowerCase();
  return key || undefined;
}

interface DtpCartStore {
  items: DtpCartItem[];
  clinicState: string | null;
  // States this clinic can ship to (own licenses ∪ telemed coverage). Drives
  // the checkout shipping-state selector. Empty/null until the catalog loads.
  allowedStates: string[];
  // Veterinary clinic: checkout collects a human owner + an animal (the patient)
  // instead of the human medical screening. Set from the catalog response.
  isVet: boolean;

  // Computed
  subtotal: number;
  itemCount: number;

  // Actions
  /** Adds the item; returns the other dose of the same drug it replaced, if any. */
  addItem: (item: DtpCartItem) => DtpCartItem | null;
  /** Removes `productId` and adds `item` in one step (the shop's weight-loss swap). */
  swapItem: (productId: string, item: DtpCartItem) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  hasItem: (productId: string) => boolean;
  /**
   * Copy the checkout-relevant fields off a catalog response in one shot.
   * Every storefront entry point (lightweight shop, educational /learn) must
   * call this on load — checkout reads these from the store, so an entry point
   * that sets only some of them silently degrades it. Deliberately the ONLY
   * way to set them: per-field setters let /learn ship for months setting
   * clinicState alone, which locked its checkout to a single state.
   */
  applyClinicFromCatalog: (clinic: DtpCatalogClinic) => void;
}

/** The subset of the catalog `clinic` payload that checkout depends on. */
export interface DtpCatalogClinic {
  physicianId?: string;
  storefrontVariant?: 'lightweight' | 'educational' | 'custom';
  state?: string;
  allowedStates?: string[];
  isVet?: boolean;
}

export const useDtpCartStore = create<DtpCartStore>()(
  persist(
    (set, get) => ({
      items: [],
      clinicState: null,
      allowedStates: [],
      isVet: false,

      get subtotal() {
        return get().items.reduce((sum, item) => sum + item.price, 0);
      },

      get itemCount() {
        return get().items.length;
      },

      addItem: (item) => {
        const { items } = get();
        // Enforce: max 1 of each product
        if (items.some(i => i.productId === item.productId)) {
          return null; // Already in cart
        }
        const replaced = item.family ? items.find(i => i.family === item.family) || null : null;
        set({ items: [...items.filter(i => i !== replaced), item] });
        return replaced;
      },

      swapItem: (productId, item) => {
        set({ items: [...get().items.filter(i => i.productId !== productId && i.productId !== item.productId), item] });
      },

      removeItem: (productId) => {
        set({ items: get().items.filter(i => i.productId !== productId) });
      },

      clearCart: () => {
        clearQualificationDraft();
        set({ items: [] });
      },

      hasItem: (productId) => {
        return get().items.some(i => i.productId === productId);
      },

      applyClinicFromCatalog: (clinic) => {
        // Every entry point lands here, so it's where a store session starts.
        setStoreContext(clinic.physicianId, clinic.storefrontVariant);
        set({
          // Leave a previously-set clinicState alone if the payload omits it,
          // rather than blanking the checkout's fallback.
          ...(clinic.state ? { clinicState: clinic.state } : {}),
          allowedStates: clinic.allowedStates || [],
          isVet: clinic.isVet === true
        });
      }
    }),
    {
      name: 'dtp-cart'
    }
  )
);
